/**
 * How big a box has to be for the words in it.
 *
 * ITS OWN MODULE so it can be tested without a canvas, and because it is
 * the part that was wrong: every node was a fixed 190 by 64 whatever the
 * label, so "Confirmed" sat in a box twice the size it needed and
 * "Deposit taken and confirmation sent" ran out of one.
 *
 * MEASURED BY ESTIMATE, not by the browser. Excalidraw wraps bound text
 * to its container itself, so the only job here is to pick a container
 * the text will fit in; being a few pixels generous is invisible, and
 * asking the DOM to measure text would make this untestable and slow for
 * no benefit anybody can see.
 */

/** Excalidraw's Normal font at 16px, measured across mixed-case labels. */
const CHAR_WIDTH = 0.56
const LINE_HEIGHT = 1.25

/** Narrower than this and a three-word label breaks across three lines. */
const MIN_WIDTH = 150
/** Wider than this and the diagram is a column of paragraphs. */
const MAX_WIDTH = 230
const MIN_HEIGHT = 56
const PADDING_X = 32
const PADDING_Y = 28

/**
 * Break a label the way the box will.
 *
 * Greedy by word, and a single word longer than the line is left alone
 * rather than hyphenated: a venue called something unbroken should look
 * wrong in the diagram, not be silently chopped.
 */
export function wrapLabel(label: string, maxChars: number): string[] {
  const words = label.split(/\s+/).filter(Boolean)
  if (words.length === 0) return ['']
  const lines: string[] = []
  let line = ''
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word
    if (candidate.length <= maxChars || line === '') {
      line = candidate
    } else {
      lines.push(line)
      line = word
    }
  }
  if (line) lines.push(line)
  return lines
}

export interface Measured {
  width: number
  height: number
  lines: string[]
}

/**
 * The box for a label, by shape.
 *
 * A DIAMOND NEEDS HALF AGAIN. Text sits in the middle of it, where the
 * shape is widest for only a fraction of its height, so a diamond sized
 * like a rectangle has its label hanging over both points. An ellipse
 * needs a little more for the same reason, less of it.
 */
export function measureNode(
  label: string,
  kind: 'step' | 'decision' | 'terminal' | 'note' = 'step',
  fontSize = 16,
): Measured {
  const maxChars = Math.floor((MAX_WIDTH - PADDING_X) / (fontSize * CHAR_WIDTH))
  const lines = wrapLabel(label, Math.max(8, maxChars))
  const longest = Math.max(...lines.map((line) => line.length))

  let width = Math.min(
    MAX_WIDTH,
    Math.max(MIN_WIDTH, Math.ceil(longest * fontSize * CHAR_WIDTH) + PADDING_X),
  )
  let height = Math.max(
    MIN_HEIGHT,
    Math.ceil(lines.length * fontSize * LINE_HEIGHT) + PADDING_Y,
  )

  /**
   * A DIAMOND NEEDS DOUBLE, not half again.
   *
   * Text lives in the middle half of a diamond's width, so a label
   * measured against the full box hangs over both points: in the first
   * version "Is Square for Restaurants Plus or Premium?" ran clean
   * through the sides. An ellipse's usable width is about two thirds.
   * These factors are the inverse of `innerBox` in scene.ts, and the two
   * have to move together.
   */
  if (kind === 'decision') {
    width = Math.round(width * 2)
    height = Math.round(height * 2)
  } else if (kind === 'terminal') {
    width = Math.round(width / 0.68)
    height = Math.round(height / 0.68)
  }

  return { width, height, lines }
}
